import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, Mail, ArrowLeft, CheckCircle, Loader2, Video } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import CalendarWidget from './CalendarWidget';
import { contactService } from '../services/api';

const BookingPage = () => {
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    consultationType: 'strategy-call',
    serviceType: '',
    message: ''
  });

  // Pre-select service type if coming from pricing page
  useEffect(() => {
    if (location.state?.preSelectedService) {
      setBookingData(prev => ({
        ...prev,
        serviceType: location.state.preSelectedService
      }));
    }
  }, [location.state]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const consultationTypes = [
    {
      id: 'strategy-call',
      title: 'Strategy Consultation Call',
      duration: '60 minutes',
      description: 'Comprehensive discussion about your operational excellence goals',
      icon: Phone
    },
    {
      id: 'six-sigma-assessment',
      title: 'Six Sigma Process Assessment',
      duration: '90 minutes',
      description: 'Detailed evaluation of your current processes and improvement opportunities',
      icon: User
    },
    {
      id: 'iso-consultation',
      title: 'ISO Certification Consultation',
      duration: '75 minutes',
      description: 'Guidance on ISO standards implementation and certification roadmap',
      icon: CheckCircle
    },
    {
      id: 'ai-systems-consultation',
      title: 'Gen AI & Agentic AI Strategy Consultation',
      duration: '60 minutes',
      description: 'AI governance, risk management, and ISO 42001 implementation',
      icon: Video
    }
  ];

  const serviceTypes = [
    'Gen AI & Agentic AI Strategy',
    'ISO Certification & Audits',
    'ISO Training Programs',
    'Lean Principles Training Programs',
    'Lean Process Excellence',
    'Operational Excellence Management',
    'Quality Control & SLA Management',
    'Quality Tools Training',
    'Risk Management & Assessment',
    'RPA & Process Automation',
    'Six Sigma Implementation',
    'Six Sigma Training Programs'
  ];

  const handleInputChange = (e) => {
    setBookingData({
      ...bookingData,
      [e.target.name]: e.target.value
    });
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      setBookingStatus('error');
      setStatusMessage('Please select a date and time for your consultation.');
      return;
    }

    if (!bookingData.serviceType) {
      setBookingStatus('error');
      setStatusMessage('Please select a service type for your consultation.');
      return;
    }

    setIsSubmitting(true);
    setBookingStatus(null);

    try {
      const selectedConsultation = consultationTypes.find(
        type => type.id === bookingData.consultationType
      );

      // Convert selectedDate to ISO format for backend consistency
      const dateObj = new Date(selectedDate);
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;

      const bookingRequest = {
        ...bookingData,
        date: selectedDate,
        time: selectedTime,
        consultationType: selectedConsultation.title,
        duration: selectedConsultation.duration,
        scheduledDateTime: `${selectedDate} at ${selectedTime}`,
        message: `${bookingData.message}

BOOKING DETAILS:
- Service Type: ${bookingData.serviceType}
- Consultation Type: ${selectedConsultation.title}
- Duration: ${selectedConsultation.duration}
- Requested Date: ${selectedDate}
- Requested Time: ${selectedTime} IST
- Phone: ${bookingData.phone}
- Company: ${bookingData.company || 'Not specified'}`
      };

      // Send email via contact service
      const result = await contactService.submitForm(bookingRequest);
      
      // Also create booking in database with ISO date format
      const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      await fetch(`${API_BASE}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: bookingData.name,
          email: bookingData.email,
          phone: bookingData.phone,
          company: bookingData.company,
          service: bookingData.serviceType,
          consultation_type: selectedConsultation.title,
          preferred_date: isoDate,  // Send ISO format to backend
          preferred_time: selectedTime,
          message: bookingRequest.message
        })
      });
      
      if (result.success) {
        setBookingStatus('success');
        setStatusMessage(`Booking request submitted successfully! You will receive a confirmation email shortly for your ${selectedConsultation.title} scheduled on ${selectedDate} at ${selectedTime}.`);
        
        // Reset form
        setBookingData({
          name: '',
          email: '',
          phone: '',
          company: '',
          consultationType: 'strategy-call',
          serviceType: '',
          message: ''
        });
        setSelectedDate(null);
        setSelectedTime(null);
      } else {
        setBookingStatus('error');
        setStatusMessage(result.message);
      }
    } catch (error) {
      setBookingStatus('error');
      setStatusMessage('An unexpected error occurred. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
      // Clear status after 8 seconds
      setTimeout(() => {
        setBookingStatus(null);
        setStatusMessage('');
      }, 8000);
    }
  };

  const selectedConsultation = consultationTypes.find(
    type => type.id === bookingData.consultationType
  );

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '120px' }}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          
          <div className="text-center">
            <h1 className="display-large mb-4">
              Book Your <span className="turquoise-text">Strategy Call</span>
            </h1>
            <p className="body-large text-gray-300 max-w-3xl mx-auto">
              Schedule a personalized consultation to discuss your operational excellence journey. 
              Choose your preferred date, time, and consultation type.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left Side - Creative Image Section */}
          <div className="lg:col-span-4 hidden lg:block">
            <div className="sticky top-8 space-y-6">
              {/* Main Image Container with Creative Styling */}
              <div className="relative group">
                {/* Glow Effect Background */}
                <div className="absolute -inset-4 bg-gradient-to-r from-turquoise-500/20 to-cyan-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                
                {/* Image Container */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 p-1">
                  <img 
                    src="/book-a-call.png"
                    alt="Book Your Strategy Call"
                    className="w-full h-auto rounded-xl transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Info Cards Below Image */}
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-turquoise-500/10 to-cyan-500/10 border border-turquoise-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-turquoise-500/20 flex items-center justify-center">
                      <Calendar className="text-turquoise-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Flexible Scheduling</h3>
                      <p className="text-sm text-gray-400">Choose time that works for you</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Video className="text-purple-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Virtual Meeting</h3>
                      <p className="text-sm text-gray-400">Online consultation via video call</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <CheckCircle className="text-blue-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Expert Guidance</h3>
                      <p className="text-sm text-gray-400">21+ years of industry experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Booking Form Content */}
          <div className="lg:col-span-8 grid lg:grid-cols-2 gap-8">
          {/* Calendar Selection */}
          <div className="space-y-8">
            <div className="dark-card">
              <h2 className="heading-2 mb-6 flex items-center gap-3">
                <Calendar className="turquoise-text" size={24} />
                Select Date & Time
              </h2>
              
              <CalendarWidget
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onDateSelect={setSelectedDate}
                onTimeSelect={setSelectedTime}
              />
            </div>

            {/* Consultation Types */}
            <div className="dark-card">
              <h3 className="heading-3 mb-6">Consultation Types</h3>
              
              <div className="space-y-3">
                {consultationTypes.map((consultation) => {
                  const IconComponent = consultation.icon;
                  return (
                    <div
                      key={consultation.id}
                      className={`p-4 cursor-pointer transition-all duration-300 ${
                        bookingData.consultationType === consultation.id
                          ? 'bg-turquoise-600/20 border border-turquoise-500'
                          : 'bg-gray-800/50 border border-gray-600 hover:border-gray-500'
                      }`}
                      onClick={() => setBookingData({ ...bookingData, consultationType: consultation.id })}
                    >
                      <div className="flex items-start gap-3">
                        <IconComponent 
                          size={20} 
                          className={bookingData.consultationType === consultation.id ? 'turquoise-text' : 'text-gray-400'} 
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="body-medium font-semibold">{consultation.title}</h4>
                            <span className="body-small text-turquoise-400 flex items-center gap-1">
                              <Clock size={14} />
                              {consultation.duration}
                            </span>
                          </div>
                          <p className="body-small text-gray-400">{consultation.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div className="space-y-8">
            <div className="dark-card">
              <h2 className="heading-2 mb-6 flex items-center gap-3">
                <User className="turquoise-text" size={24} />
                Your Information
              </h2>

              {/* Status Messages */}
              {bookingStatus && (
                <div className={`mb-6 p-4 border ${
                  bookingStatus === 'success' 
                    ? 'bg-green-900/20 border-green-600 text-green-300'
                    : 'bg-red-900/20 border-red-600 text-red-300'
                }`}>
                  <div className="flex items-start gap-2">
                    {bookingStatus === 'success' ? (
                      <CheckCircle size={20} className="mt-0.5" />
                    ) : (
                      <Mail size={20} className="mt-0.5" />
                    )}
                    <span className="body-medium">{statusMessage}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleBooking} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    name="name"
                    placeholder="Full Name *"
                    value={bookingData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                  
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={bookingData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number *"
                    value={bookingData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                  
                  <Input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={bookingData.company}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                </div>

                {/* Service Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Service Type <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="serviceType"
                    value={bookingData.serviceType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-turquoise-500 focus:outline-none"
                  >
                    <option value="">Select a service type...</option>
                    {serviceTypes.map((serviceType) => (
                      <option key={serviceType} value={serviceType}>
                        {serviceType}
                      </option>
                    ))}
                  </select>
                </div>
                
                <Textarea
                  name="message"
                  placeholder="Brief description of your challenges and goals..."
                  value={bookingData.message}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  rows={4}
                  className="bg-transparent border-gray-600 text-white placeholder-gray-400 resize-none"
                />
                
                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting || !selectedDate || !selectedTime}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Submitting Booking...
                    </>
                  ) : (
                    <>
                      <Calendar size={20} />
                      Book Consultation
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Selected Booking Summary */}
            {selectedDate && selectedTime && selectedConsultation && (
              <div className="dark-card bg-turquoise-900/10 border-turquoise-500/30">
                <h3 className="heading-3 mb-4 turquoise-text">Booking Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Calendar size={18} className="turquoise-text" />
                    <span className="body-medium">{selectedDate} at {selectedTime}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <selectedConsultation.icon size={18} className="turquoise-text" />
                    <span className="body-medium">{selectedConsultation.title}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Clock size={18} className="turquoise-text" />
                    <span className="body-medium">Duration: {selectedConsultation.duration}</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-turquoise-500/30">
                  <p className="body-small text-gray-300">
                    You will receive a confirmation email with meeting details and preparation materials.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>

        {/* Additional Information */}
        <div className="mt-16 text-center p-8 bg-gradient-to-r from-turquoise-500/10 to-transparent border border-turquoise-500/30">
          <h3 className="heading-2 mb-4">What to Expect</h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="space-y-3">
              <div className="w-12 h-12 bg-turquoise-600 text-black rounded-full flex items-center justify-center mx-auto">
                <Phone size={24} />
              </div>
              <h4 className="body-medium font-semibold">Confirmation Call</h4>
              <p className="body-small text-gray-400">You'll receive a confirmation email within 24 hours with meeting details.</p>
            </div>
            
            <div className="space-y-3">
              <div className="w-12 h-12 bg-turquoise-600 text-black rounded-full flex items-center justify-center mx-auto">
                <User size={24} />
              </div>
              <h4 className="body-medium font-semibold">Personalized Session</h4>
              <p className="body-small text-gray-400">Tailored consultation based on your specific challenges and industry.</p>
            </div>
            
            <div className="space-y-3">
              <div className="w-12 h-12 bg-turquoise-600 text-black rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={24} />
              </div>
              <h4 className="body-medium font-semibold">Action Plan</h4>
              <p className="body-small text-gray-400">Clear next steps and recommendations for your operational excellence journey.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;